import type { LocalizedString } from './runtimeConfig'
import { DEFAULT_LANG } from '../config/locales'

/**
 * Picks the right string variant for the given language (runtime-config path:
 * banners and other admin-pushed strings, whose shape is open-ended).
 *
 * - Plain string → returned as-is.
 * - Object map   → tries `lang`, then the default locale (Danish), then any
 *                  defined value. Returns '' only when the map is empty (which
 *                  the sanitizer should have rejected upstream).
 *
 * The default-locale fallback matches `loc()` on the static-content path, so
 * both localisation systems fall back to Danish consistently.
 *
 * Example:
 *   resolveLocalizedString({ en: "Hi", da: "Hej" }, "da") === "Hej"
 *   resolveLocalizedString({ en: "Hi", da: "Hej" }, "fr") === "Hej"  // default
 *   resolveLocalizedString({ en: "Hi" },            "fr") === "Hi"   // any
 *   resolveLocalizedString("Hi",                    "da") === "Hi"
 */
export function resolveLocalizedString(value: LocalizedString, lang: string): string {
  if (typeof value === 'string') return value
  if (typeof value[lang] === 'string') return value[lang]
  if (typeof value[DEFAULT_LANG] === 'string') return value[DEFAULT_LANG]
  for (const v of Object.values(value)) {
    if (typeof v === 'string') return v
  }
  return ''
}
